/**
 * WP8d / #46620 — verification shell search commands and result grouping.
 *
 * Command rows are the retained palette actions (R5). Create finding is not
 * supported: findings are produced by runs, not a user "new finding" action.
 * Create goal stays in-shell at workflows/new so search does not dump to
 * old agents chrome.
 *
 * Owned by the verification search UI. Do not import tree.ts. Server hits
 * stay in excellent/src/search (WP8a-c).
 */

import { href, NAV_ITEMS, pathOf } from "./nav";
import type { IconName } from "./icons";

export type SearchCommandId =
  | "define-workflow"
  | "create-goal"
  | "open-framework-settings"
  | "open-settings"
  | "open-help";

export type SearchCommand = {
  id: SearchCommandId;
  label: string;
  href: string;
  icon: IconName;
  shortcut?: string;
  /** Search aliases that stay out of the visible command label. */
  keywords?: readonly string[];
  /** Compatibility exit to a retained (non-verification-IA) surface. */
  retained?: boolean;
};

/** Findings are run-produced. No create-finding command until a WP re-approves it. */
export const CREATE_FINDING_SUPPORTED = false;

export const DEFINE_WORKFLOW_HREF = href("workflows", "new");
export const CREATE_GOAL_HREF = href("workflows", "new");
export const OPEN_SETTINGS_HREF = "/settings";
export const OPEN_FRAMEWORK_SETTINGS_HREF = "/settings#frameworks";
export const OPEN_HELP_HREF = "/help";

export const SEARCH_COMMANDS: readonly SearchCommand[] = [
  {
    id: "define-workflow",
    label: "Define workflow",
    href: DEFINE_WORKFLOW_HREF,
    icon: "plus",
    shortcut: "W",
  },
  {
    id: "create-goal",
    label: "Create goal",
    href: CREATE_GOAL_HREF,
    icon: "gauge",
    retained: true,
  },
  {
    id: "open-framework-settings",
    label: "Open framework settings",
    href: OPEN_FRAMEWORK_SETTINGS_HREF,
    icon: "shield",
    keywords: ["SOC 2", "period", "audit"],
  },
  {
    id: "open-settings",
    label: "Open settings",
    href: OPEN_SETTINGS_HREF,
    icon: "cpu",
    shortcut: ",",
  },
  {
    id: "open-help",
    label: "Open help",
    href: OPEN_HELP_HREF,
    icon: "info",
  },
];

/**
 * Verification-object groups first, then Help / Settings, then leftover
 * aggregator groups (Issues, Projects, …) so a Demo workflow query surfaces
 * the workflow before a settings page of the same token.
 */
export const SEARCH_GROUP_ORDER = [
  "Workflows",
  "Controls",
  "Policies",
  "Evidence",
  "Findings",
  "Runs",
  "Receipts",
  "Packs",
  "Audit rooms",
  "Help",
  "Settings",
] as const;

export type SearchHitLike = {
  id: string;
  href: string;
  label: string;
  hint?: string;
  group: string;
};

export type GroupedHits<T extends SearchHitLike = SearchHitLike> = {
  group: string;
  hits: T[];
};

export function matchingCommands(query: string): SearchCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...SEARCH_COMMANDS];
  return SEARCH_COMMANDS.filter((c) => {
    const hay = `${c.label} ${c.id} ${c.href} ${c.keywords?.join(" ") ?? ""}`.toLowerCase();
    return hay.includes(q);
  });
}

export function groupSearchHits<T extends SearchHitLike>(hits: readonly T[]): GroupedHits<T>[] {
  const buckets = new Map<string, T[]>();
  for (const hit of hits) {
    const list = buckets.get(hit.group);
    if (list) list.push(hit);
    else buckets.set(hit.group, [hit]);
  }
  const known = new Set<string>(SEARCH_GROUP_ORDER);
  const ordered: GroupedHits<T>[] = [];
  for (const group of SEARCH_GROUP_ORDER) {
    const list = buckets.get(group);
    if (list?.length) ordered.push({ group, hits: list });
  }
  for (const [group, list] of buckets) {
    if (!known.has(group) && list.length) ordered.push({ group, hits: list });
  }
  return ordered;
}

export type ShellSearchQuery = { q: string; env: "demo" | "real" };

/**
 * Always send env so Real never asks for Demo-only objects. Null environment
 * (chooser not yet answered) follows the server default: real.
 */
export function shellSearchQuery(input: {
  q: string;
  environment: "demo" | "real" | null;
}): ShellSearchQuery {
  return {
    q: input.q,
    env: input.environment === "demo" ? "demo" : "real",
  };
}

/**
 * Every in-shell verification route that inherits the palette because it is
 * mounted inside VerificationShell (NAV chrome + settings + retained
 * account-menu destinations). `/help` is a command target; create-goal
 * uses the in-shell workflows/new path.
 */
export function verificationSearchRoutes(): string[] {
  const routes = new Set<string>(["/", "/settings"]);
  for (const item of NAV_ITEMS) routes.add(pathOf(item));
  return [...routes];
}

export type SearchRow =
  | { key: string; kind: "command"; command: SearchCommand }
  | { key: string; kind: "hit"; hit: SearchHitLike; group: string };

/** Commands first (filtered), then grouped hits when the query is non-empty. */
export function buildSearchRows(query: string, hits: readonly SearchHitLike[]): SearchRow[] {
  const rows: SearchRow[] = matchingCommands(query).map((command) => ({
    key: `cmd:${command.id}`,
    kind: "command" as const,
    command,
  }));
  if (!query.trim()) return rows;
  for (const grouped of groupSearchHits(hits)) {
    for (const hit of grouped.hits) {
      rows.push({ key: `hit:${hit.id}`, kind: "hit", hit, group: grouped.group });
    }
  }
  return rows;
}
